import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import {
  BackLink,
  ErrorNotice,
  LoadingState,
  PageEyebrow,
  Panel,
  Pill,
  SelectInput,
} from '@bcecloud/portal-ui';
import { getJson, sendJson, errorMessage } from '@/lib/api';
import { InquiryWorkroom } from '@/components/InquiryWorkroom';

/** The enquiry pipeline stages, in order, with staff-facing labels. */
const STAGES = [
  ['INITIAL_INQUIRY', 'Inquiry received'],
  ['DISCOVERY_SCHEDULED', 'Discovery call scheduled'],
  ['OBJECTIVES_CONFIRMED', 'Objectives confirmed'],
  ['OPTIONS_PRESENTED', 'Options presented'],
  ['FEASIBILITY_REVIEW', 'Feasibility review'],
  ['BUDGET_REVIEW', 'Budget review'],
  ['PROPOSAL_IN_DEVELOPMENT', 'Proposal in development'],
  ['PROPOSAL_SUBMITTED', 'Proposal submitted'],
  ['PROCUREMENT_REVIEW', 'Procurement review'],
  ['AGREEMENT_SIGNED', 'Agreement signed'],
  ['PLANNING_UNDERWAY', 'Planning underway'],
  ['STAFFING_CONFIRMED', 'Staffing confirmed'],
  ['DELIVERED', 'Delivered'],
  ['RECAP_COMPLETED', 'Recap completed'],
  ['CLOSED_LOST', 'Closed'],
] as const;

/** One enquiry in full, as the admin API returns it. */
interface EnquiryDetail {
  readonly id: string;
  readonly reference: string;
  readonly status: string;
  readonly eventName: string;
  readonly eventType: string | null;
  readonly dateStart: string | null;
  readonly dateEnd: string | null;
  readonly datesFlexible: boolean;
  readonly locations: readonly string[];
  readonly estimatedParticipants: number | null;
  readonly audience: string | null;
  readonly purpose: string | null;
  readonly desiredOutcomes: string | null;
  readonly timeAvailable: string | null;
  readonly format: string | null;
  readonly venueStatus: string | null;
  readonly preferredProgram: string | null;
  readonly customizationLevel: string | null;
  readonly companyTheme: string | null;
  readonly leadershipInvolvement: string | null;
  readonly csrInterests: string | null;
  readonly accessibilityNeeds: string | null;
  readonly budgetRange: string | null;
  readonly availableCredits: number | null;
  readonly decisionTimeline: string | null;
  readonly otherVendors: string | null;
  readonly execSponsor: string | null;
  readonly procurementContact: string | null;
  readonly notes: string | null;
  readonly planningContact: {
    readonly id: string;
    readonly name: string;
    readonly email: string;
  } | null;
  readonly createdAt: string;
}

/** A labelled group of enquiry facts, rendered as one panel. */
interface FactGroup {
  readonly title: string;
  readonly facts: readonly (readonly [string, string | null])[];
}

/**
 * A date for the fact sheet.
 *
 * @param iso - ISO timestamp.
 * @returns e.g. "August 8, 2026".
 */
function longDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

/**
 * One large-event enquiry, in full — everything the client wrote, grouped the
 * way BCE reads it: the event, the goals, delivery constraints, then the
 * commercial picture. The list view carries headline numbers; the context a
 * client added lives here.
 *
 * @returns The enquiry detail page.
 */
export function EnquiryDetailPage(): React.ReactElement {
  const { organizationId = '', inquiryId = '' } = useParams();
  const queryClient = useQueryClient();

  const enquiry = useQuery({
    queryKey: ['org-enquiry', organizationId, inquiryId],
    queryFn: ({ signal }) =>
      getJson<EnquiryDetail>(
        `/organizations/${organizationId}/large-events/${inquiryId}`,
        undefined,
        signal,
      ),
  });

  const advance = useMutation({
    mutationFn: (status: string) =>
      sendJson('patch', `/organizations/${organizationId}/large-events/${inquiryId}`, { status }),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['org-enquiry', organizationId, inquiryId] }),
        queryClient.invalidateQueries({ queryKey: ['org-enquiries', organizationId] }),
      ]);
    },
  });

  if (enquiry.isPending) return <LoadingState label="Loading the inquiry…" />;
  if (enquiry.isError) {
    return <ErrorNotice title="Inquiry unavailable" message={errorMessage(enquiry.error)} />;
  }

  const data = enquiry.data;

  const groups: readonly FactGroup[] = [
    {
      title: 'The event',
      facts: [
        ['Type', data.eventType],
        [
          'When',
          data.dateStart
            ? `${longDate(data.dateStart)}${data.dateEnd ? ` – ${longDate(data.dateEnd)}` : ''}${data.datesFlexible ? ' (flexible)' : ''}`
            : data.datesFlexible
              ? 'Flexible'
              : null,
        ],
        ['Where', data.locations.length > 0 ? data.locations.join(', ') : null],
        [
          'Participants',
          data.estimatedParticipants === null ? null : String(data.estimatedParticipants),
        ],
        ['Audience', data.audience],
        ['Format', data.format],
        ['Time available', data.timeAvailable],
      ],
    },
    {
      title: 'What it should achieve',
      facts: [
        ['Goal', data.purpose],
        ['Desired outcomes', data.desiredOutcomes],
        ['Company theme', data.companyTheme],
        ['Leadership involvement', data.leadershipInvolvement],
        ['CSR interests', data.csrInterests],
      ],
    },
    {
      title: 'Delivery',
      facts: [
        ['Venue status', data.venueStatus],
        ['Preferred program', data.preferredProgram],
        ['Customization', data.customizationLevel],
        ['Accessibility needs', data.accessibilityNeeds],
      ],
    },
    {
      title: 'Commercial',
      facts: [
        ['Budget', data.budgetRange],
        [
          'Available credits',
          data.availableCredits === null ? null : String(data.availableCredits),
        ],
        ['Decision timeline', data.decisionTimeline],
        ['Other vendors considered', data.otherVendors],
        ['Executive sponsor', data.execSponsor],
        ['Procurement contact', data.procurementContact],
        ['Notes', data.notes],
      ],
    },
  ];

  const stageLabel = STAGES.find(([value]) => value === data.status)?.[1] ?? data.status;

  return (
    <section className="animate-portal-rise">
      <BackLink to={`/organizations/${organizationId}?tab=enquiries`}>
        All inquiries
      </BackLink>

      <header className="mt-4 flex flex-wrap items-start justify-between gap-4">
        <div>
          <PageEyebrow>LARGE EVENT INQUIRY</PageEyebrow>
          <h1 className="text-portal-page tracking-[-1.7px]">{data.eventName}</h1>
          <p className="mt-1 text-sm text-admin-muted">
            {data.reference} · submitted {longDate(data.createdAt)}
            {data.planningContact
              ? ` · planning contact ${data.planningContact.name} (${data.planningContact.email})`
              : ''}
          </p>
        </div>

        <div className="flex items-center gap-3">
          <Pill tone={data.status === 'CLOSED_LOST' ? 'neutral' : 'brand'}>{stageLabel}</Pill>
          <div>
            <label htmlFor="enquiry-stage" className="sr-only">
              Stage
            </label>
            <SelectInput
              density="compact"
              id="enquiry-stage"
              value={data.status}
              disabled={advance.isPending}
              onChange={(event) => advance.mutate(event.target.value)}
            >
              {STAGES.map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </SelectInput>
          </div>
        </div>
      </header>

      {advance.isError ? (
        <div className="mt-4">
          <ErrorNotice inline title="Could not update the stage" message={errorMessage(advance.error)} />
        </div>
      ) : null}

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
        {groups.map((group) => {
          const facts = group.facts.filter(
            (fact): fact is readonly [string, string] => fact[1] != null && fact[1] !== '',
          );
          return (
            <Panel key={group.title} className="p-6">
              <h2 className="text-portal-section font-bold tracking-[-0.4px]">{group.title}</h2>
              {facts.length === 0 ? (
                <p className="mt-3 text-portal-small text-portal-muted">
                  Nothing here yet — the discovery call fills this in.
                </p>
              ) : (
                <dl className="mt-4 grid grid-cols-[10rem_1fr] gap-x-4 gap-y-3">
                  {facts.map(([label, value]) => (
                    <div key={label} className="contents">
                      <dt className="text-portal-micro font-bold text-portal-muted">{label}</dt>
                      <dd className="text-portal-small leading-[1.6] whitespace-pre-line">
                        {value}
                      </dd>
                    </div>
                  ))}
                </dl>
              )}
            </Panel>
          );
        })}
      </div>

      <div className="mt-6">
        <InquiryWorkroom organizationId={organizationId} subject={{ type: 'large_event_inquiry', id: inquiryId }} />
      </div>
    </section>
  );
}
